<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :disabled="disabled"
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
    open-on-click
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props">
        <slot></slot>
      </div>
    </template>

    <v-card>
      <v-card-title class="d-flex justify-space-between">
        {{ $t('routes.node.adobePremiereAdd') }}

        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div v-if="errorMessage" class="bg-error"> {{ errorMessage }}</div>
      </v-card-text>

      <v-card-text v-if="!pods || pods.length == 0">
        <div class="bg-error" data-cy="">
          {{ $t('routes.node.adobeDialogNoItems') }}
        </div>
      </v-card-text>

      <v-card-text>
        <li v-for="pod in pods" v-bind:key="pod.id">
          {{ pod.name }} {{ addCheckMark(pod.id) }}
        </li>
      </v-card-text>

        <v-card-text class="text-center">
          <v-progress-circular v-if="loading"
                              indeterminate
                              color="primary"
                              :aria-label="$t('common.loading')">
        </v-progress-circular>
        </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!loading && showOK"
          variant="flat"
          color="primary"
          elevation="0"
          @click="addAllToPremierePro"
        >
          {{ $t('common.add') }}
        </v-btn>
        <v-btn variant="text" @click="cancel">{{ $t('common.cancel') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { usePodCommentStore } from '@/stores/PodCommentStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "AdobePremiereDialog",
  props: {
    pods: {
      type: Array,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: function() {
    return {
      loading: false,
      errorMessage: "",
      podsToImport: [],
      importedPods: [],
      showOK: true,
      failedPods: [],
    }
  },
  mounted: function() {
  },
  methods: {
    addAllToPremierePro: function() {

      if (!this.pods || this.pods.length == 0) {
        this.dialog = false;
        return;
      }

      const podNames = this.pods.map((p) => p.name);
      const namesSet = Array.from(new Set(podNames));
      if (namesSet.length < podNames.length) {
        this.errorMessage = this.$t('routes.node.adobeDownloadNotAllowed');
        return;
      }

      // eslint-disable-next-line no-undef
      var csInterface = new CSInterface;

      this.dialog = false;
      const script = `chooseFolder()`;

      let self = this;

      csInterface.evalScript(script, (result) => {

        if (!result) {
          return;
        } else {

          self.podsToImport = self.pods.map((p) => {
            return {
              name: p.name,
              id: p.id,
              url: p.asset.raw[0].url,
            }
          });

          self.loading = true;
          self.dialog = true;

          self.importPodAndComments();
        }
      });
    },
    importPodAndComments: function() {
      if (!this.podsToImport || this.podsToImport.length == 0) {
        this.loading = false;
        if (this.failedPods.length > 0) {
          this.showOK = false;

          this.errorMessage = this.$t('routes.node.adobeExistingFiles');
          return;
        }
        let self = this;
        setTimeout(() => {
          self.dialog = false;
          self.errorMessage = "";
          self.importedPods = [];
          self.showOK = true;
        }, 500);
        return;
      }
      const podData = this.podsToImport.shift();
      // eslint-disable-next-line no-useless-escape
      const podNameReplaced = podData.name.replace(/[\/\\":*?<>|]/g,'_');
      const nameScript = `createFilePath("${podNameReplaced}")`;

      let self = this;
      // eslint-disable-next-line no-undef
      var csInterface = new CSInterface;
      csInterface.evalScript(nameScript, (res) => {

          if (res == "Exists") {
            self.failedPods.push(podData.id);
            self.importPodAndComments();
            return;
          }

          const filePath = `${res}`;

          const fs = window.cep_node.require('fs');
          const file = fs.createWriteStream(filePath);

          file.on('finish', function () {
            // eslint-disable-next-line no-undef
            var csInterface = new CSInterface;
            csInterface.evalScript(`importFile()`, (res) => {
              if (res === "Ready") {
                const podCommentStore = usePodCommentStore();
                podCommentStore.getCommentsStateless(podData.id)
                .then(function(comments) {
                  for (const comment of comments) {
                    let start = comment.start;
                    let end = comment.end ?? comment.start;
                    let text = comment.content;
                    const editedText = text.split("\n").join("\\n");
                    let name = self.$t('common.deletedUser');
                    if (comment.createdby) {
                      name = `${comment.createdby.firstname} ${comment.createdby.lastname}`;
                    } else if (comment.email) {
                      name = comment.email;
                    }

                    const nameScript = `addName("${name}")`;
                    csInterface.evalScript(nameScript);

                    const textScript = `addText("${editedText}")`;
                    csInterface.evalScript(textScript);

                    const startScript = `addStart("${start/1000}")`;
                    csInterface.evalScript(startScript);

                    const endScript = `addEnd("${end/1000}")`;
                    csInterface.evalScript(endScript);
                  }

                  self.importedPods.push(podData.id);
                  self.importPodAndComments();
                });
              } else {
                console.log("Something weird happened");
                self.showOK = false;
                self.loading = false;
                self.errorMessage = self.$t('routes.node.adobePremiereError');
              }
            });
          });

          fetch(podData.url).then(response => {
            const reader = response.body.getReader();

            self.readerRepeat(reader, file, filePath, podData.id);

          }).catch(()=>{
            ///Exception occured do something
            self.dialog = true;
            self.showOK = false;
            self.loading = false;
            self.errorMessage = this.$t('routes.node.adobePremiereError');
          });

      });
    },
    addCheckMark(podId) {
      const imported = this.importedPods.filter((i) => i == podId);
      if (imported.length > 0) {
        return " ✓";
      }
      const failed = this.failedPods.filter((i) => i == podId);
      if (failed.length > 0) {
        return " ✗";
      }
      return "";
    },
    readerRepeat: function (reader, file, filePath, podId) {
      let self = this;
      reader.read().then(object => {
        if (object.done) {
          file.end();
        } else {
          file.write(object.value, 'utf8', () => {
            self.readerRepeat(reader, file, filePath, podId);
          });
        }
      }).catch((exc)=>{
        ///Exception occured do something
        console.log("Exception: ", exc);
        self.showOK = false;
        self.loading = false;
        self.errorMessage = this.$t('routes.node.adobePremiereError');
      });
    },
    cancel: function() {
      this.podsToImport = [];
      this.importedPods = [];
      this.failedPods = [];
      this.dialog = false;
      this.errorMessage = "";
      this.loading = false;
      this.showOK = true;
    },
  },
  computed: {

  }
}
</script>

<style>

</style>
